import React, { FC } from 'react'
import { Box } from '@moonpig/launchpad-components'
import { formatPrice } from '@moonpig/web-core-utils'
import { useLanguage, useStoreId } from '@moonpig/web-core-stores'
import { MULTI_CARD_DISCOUNTS } from '@moonpig/web-core-brand/config'
import { useProductStore } from '../../contexts/product'
import { ModuleContext, ModulePrice } from '../types'
import { useProductsLocaleText } from '../../locale'
import { isCardProduct } from '../../utils/isCardProduct'
import { ProductPrice } from '../../components/ProductPrice'
import { PriceSuffix } from '../../components/PriceSuffix'
import { RightAlignedVariant } from './RightAlignedVariant'
import { DefaultVariant } from './DefaultVariant'
import { getDiscountedPriceCentAmount } from './getDiscountedPrice'

type Props = {
  context: ModuleContext
  module: ModulePrice
}

const DEFAULT_DISPLAY_OPTIONS = {
  showSimplifiedExperimentVariant: false,
  color: undefined,
}

export const PriceModule: FC<Props> = ({ module: { displayOptions } }) => {
  const [{ product, variant, quantity }] = useProductStore()
  const storeId = useStoreId()
  const language = useLanguage()
  const localiseText = useProductsLocaleText()

  const isCard = isCardProduct(product.category.department)

  const { minimumQuantity, discountedPercentage, fullPrice } = variant
  const { centAmount, fractionDigits, currencyCode } = variant.price
  const { discount, code } = MULTI_CARD_DISCOUNTS[storeId].reduce(
    (acc, current) => (quantity >= current.minimumQuantity ? current : acc),
    { discount: 0, code: '' },
  )

  const { showSimplifiedExperimentVariant, color } =
    displayOptions ?? DEFAULT_DISPLAY_OPTIONS

  const localeKey = showSimplifiedExperimentVariant
    ? 'multicard.discount_simplified'
    : 'multicard.discount'
  const discountText = discount
    ? `(${localiseText(localeKey, {
        discount,
      })}${code && ' with code '}${code})`
    : ''

  const regularTotalPrice = formatPrice(
    centAmount,
    fractionDigits,
    currencyCode,
    quantity,
    language,
  )

  const discountedTotalPrice = discount
    ? formatPrice(
        getDiscountedPriceCentAmount(centAmount, discount),
        fractionDigits,
        currencyCode,
        quantity,
        language,
      )
    : ''

  const CardPrice = showSimplifiedExperimentVariant
    ? RightAlignedVariant
    : DefaultVariant

  if (isCard) {
    return (
      <CardPrice
        code={code}
        quantity={quantity}
        discount={discount}
        regularTotalPrice={regularTotalPrice}
        discountedTotalPrice={discountedTotalPrice}
        discountText={discountText}
      />
    )
  }

  return (
    <Box mb={6} data-testid="module-price">
      <ProductPrice
        priceCentAmount={centAmount}
        currencyCode={currencyCode}
        fractionDigits={fractionDigits}
        minimumQuantity={minimumQuantity}
        fullPriceCentAmount={fullPrice?.centAmount}
        discountedPercentage={discountedPercentage}
        color={color}
      />
      <PriceSuffix color={color} />
    </Box>
  )
}
